import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Music, Heart, Share2, Download, Headphones, ExternalLink } from 'lucide-react';
import AccessLevelBadge from './AccessLevelBadge';
import { getAccessLabel, isGated } from '../utils/accessLabels';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import DownloadPaywall from './DownloadPaywall';
import ShareModal from './ShareModal';
import SignupInviteModal from './SignupInviteModal';
import AudioPlayer from './AudioPlayer';
import AccessRequiredModal from './AccessRequiredModal';

const optimizedCover = (url) => {
    if (!url || url.endsWith('-opt.webp')) return url;
    const dot = url.lastIndexOf('.');
    return dot !== -1 ? url.slice(0, dot) + '-opt.webp' : url;
};

const LatestDeepWaveSongs = ({ limit = 8, showTitle = true, className = "", columns = 3, excludeId = null, viewAllPosition = 'top' }) => {
    const gridColsClass = columns >= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3';
    const [songs, setSongs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [downloadPaywall, setDownloadPaywall] = useState({ isOpen: false, song: null });
    const [shareModal, setShareModal] = useState({ isOpen: false, song: null });
    const [signupInvite, setSignupInvite] = useState({ isOpen: false, contentType: 'audio' });
    const [accessModal, setAccessModal] = useState({ isOpen: false, song: null });
    const { isAuthenticated, hasActiveSubscription } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        fetchDeepWaveSongs();
    }, [limit, excludeId]);

    const fetchDeepWaveSongs = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await api.get(`/upload/public/audio?limit=20`);

            if (response.data.success) {
                const deepWaveSongs = response.data.data.filter(s => s.user_id === 7 && s.id !== excludeId);

                // Deduplicate by filename/title
                const unique = [];
                const seen = new Set();
                for (const song of deepWaveSongs) {
                    const key = song.stored_filename || song.original_filename || song.title || song.file_url;
                    if (key && !seen.has(key)) {
                        seen.add(key);
                        unique.push(song);
                    }
                }

                setSongs(unique.slice(0, limit));
            } else {
                setError('Failed to load Deep Wave songs');
            }
        } catch (err) {
            console.error('Error fetching Deep Wave songs:', err);
            setError('Unable to load songs. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const handleLike = async (song) => {
        if (!isAuthenticated) {
            setSignupInvite({ isOpen: true, contentType: 'audio' });
            return;
        }
        try {
            const response = await api.post(`/upload/${song.id}/like`);
            if (response.data.success) {
                setSongs(prev => prev.map(s =>
                    s.id === song.id
                        ? { ...s, like_count: response.data.data.likeCount, user_has_liked: response.data.data.liked }
                        : s
                ));
            }
        } catch (err) {
            console.error('Error toggling like:', err);
        }
    };

    const Header = () => showTitle ? (
        <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3">
                    <Music className="w-6 h-6 text-blue-400" />
                    <h2 className="text-xl sm:text-2xl font-bold gradient-text">Latest Songs by The Deep Wave</h2>
                    <span className="text-xs bg-blue-600/20 text-blue-400 px-2 py-1 rounded-full">Official</span>
                </div>
                {viewAllPosition === 'top' && (
                    <Link
                        to="/feed/artist/TheDeepWave/audio"
                        className="text-blue-400 hover:text-blue-300 transition-colors text-sm font-medium self-start sm:self-auto"
                    >
                        View All →
                    </Link>
                )}
            </div>
        </div>
    ) : null;

    if (loading) {
        return (
            <div className={className}>
                <Header />
                <div className={`grid grid-cols-1 sm:grid-cols-2 ${gridColsClass} gap-4`}>
                    {[...Array(limit)].map((_, i) => (
                        <div key={i} className="bg-gray-800/30 rounded-xl border border-gray-700/50 animate-pulse" style={{ height: '190px' }} />
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={className}>
                <Header />
                <div className="glass-card p-6 text-center">
                    <Music className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400 mb-4">{error}</p>
                    <button onClick={fetchDeepWaveSongs} className="btn-secondary">Try Again</button>
                </div>
            </div>
        );
    }

    if (songs.length === 0) {
        return (
            <div className={className}>
                <Header />
                <div className="glass-card p-8 text-center">
                    <Music className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-300 mb-2">No songs yet</h3>
                    <p className="text-gray-400">Check back soon for new releases!</p>
                </div>
            </div>
        );
    }

    return (
        <div className={className}>
            <Header />

            <div className={`grid grid-cols-1 sm:grid-cols-2 ${gridColsClass} gap-4`}>
                {songs.map((song, index) => (
                    <div
                        key={song.id}
                        className="bg-gray-800/30 hover:bg-gray-800/50 rounded-xl border border-gray-700/50 hover:border-blue-600/30 transition-all duration-300 overflow-hidden"
                    >
                        {/* Cover Image - full width of the card */}
                        <div className="relative w-full aspect-square">
                            {isGated(song.access_level, isAuthenticated, hasActiveSubscription) ? (
                                <Link
                                    to={`/view/audio/${song.username || 'band'}/${song.stored_filename || song.original_filename || song.id}`}
                                    className="relative block w-full h-full overflow-hidden"
                                >
                                    {song.cover_image_url ? (
                                        <img src={optimizedCover(song.cover_image_url)} onError={(e) => { e.target.onerror = null; e.target.src = song.cover_image_url; }} alt={song.title} className="w-full h-full object-cover" onContextMenu={(e) => e.preventDefault()} />
                                    ) : (
                                        <div className="w-full h-full bg-gradient-to-br from-purple-900 to-pink-900 flex items-center justify-center">
                                            <Music className="w-8 h-8 text-purple-300/60" />
                                        </div>
                                    )}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent flex items-end justify-center pb-3">
                                        <span className="text-white text-xs font-medium bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full">{getAccessLabel(song.access_level, 'listen')}</span>
                                    </div>
                                    <AccessLevelBadge accessLevel={song.access_level} isAuthenticated={isAuthenticated} hasActiveSubscription={hasActiveSubscription} />
                                </Link>
                            ) : song.cover_image_url ? (
                                <img src={optimizedCover(song.cover_image_url)} onError={(e) => { e.target.onerror = null; e.target.src = song.cover_image_url; }} alt={song.title} className="w-full h-full object-cover" onContextMenu={(e) => e.preventDefault()} />
                            ) : (
                                <div className="w-full h-full bg-gradient-to-br from-purple-900 to-pink-900 flex items-center justify-center">
                                    <Music className="w-10 h-10 text-purple-300/60" />
                                </div>
                            )}
                        </div>

                        {/* Play controls + wave player under the cover */}
                        {(() => {
                            const gated = isGated(song.access_level, isAuthenticated, hasActiveSubscription);
                            return (
                                <AudioPlayer
                                    src={song.file_url}
                                    title={song.title}
                                    hideCoverArt
                                    uploadId={gated ? null : song.id}
                                    locked={gated}
                                    onLockedClick={() => setAccessModal({ isOpen: true, song })}
                                />
                            );
                        })()}
                        {/* Footer */}
                        <div className="p-3">
                            <h3 className="font-bold text-white text-sm truncate mb-0.5">
                                {song.title || `Track ${index + 1}`}
                            </h3>
                            <div className="flex items-center justify-between text-xs text-gray-400 mb-2">
                                <span className="flex items-center gap-1">
                                    <span>🌊</span>
                                    The Deep Wave
                                </span>
                                <span className="flex items-center gap-1">
                                    <Headphones className="w-3 h-3" />
                                    {(song.play_count || 0).toLocaleString()}
                                </span>
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-0.5">
                                    <button
                                        onClick={() => handleLike(song)}
                                        className={`p-1.5 rounded transition-colors ${song.user_has_liked ? 'text-red-400' : 'text-gray-400 hover:text-red-400'}`}
                                        title={song.user_has_liked ? 'Liked' : 'Like'}
                                    >
                                        <Heart className={`w-4 h-4 ${song.user_has_liked ? 'fill-current' : ''}`} />
                                    </button>
                                    <button
                                        onClick={() => setShareModal({ isOpen: true, song })}
                                        className="p-1.5 text-gray-400 hover:text-blue-400 rounded transition-colors"
                                        title="Share"
                                    >
                                        <Share2 className="w-4 h-4" />
                                    </button>
                                </div>
                                <div className="flex items-center gap-0.5">
                                    <Link
                                        to={`/view/audio/${song.username || 'band'}/${song.stored_filename || song.original_filename || song.id}`}
                                        className="p-1.5 text-gray-400 hover:text-blue-400 rounded transition-colors"
                                        title="View details"
                                    >
                                        <ExternalLink className="w-4 h-4" />
                                    </Link>
                                    <button
                                        onClick={() => setDownloadPaywall({ isOpen: true, song })}
                                        className="p-1.5 text-gray-400 hover:text-green-400 rounded transition-colors"
                                        title="Download"
                                    >
                                        <Download className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {viewAllPosition === 'bottom' ? (
                <div className="mt-6 text-center">
                    <Link
                        to="/feed/artist/TheDeepWave/audio"
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600/10 hover:bg-blue-600/20 text-blue-400 hover:text-blue-300 rounded-lg transition-all duration-200 hover:scale-105 text-sm border border-blue-600/20"
                    >
                        <Music className="w-3.5 h-3.5" />
                        View All Songs
                    </Link>
                </div>
            ) : songs.length >= limit && (
                <div className="text-center mt-6">
                    <Link to="/feed/artist/TheDeepWave/audio" className="btn-secondary">
                        Load More Songs
                    </Link>
                </div>
            )}

            <DownloadPaywall
                isOpen={downloadPaywall.isOpen}
                onClose={() => setDownloadPaywall({ isOpen: false, song: null })}
                mediaTitle={downloadPaywall.song?.title || 'Unknown Song'}
                mediaType="song"
                uploadId={downloadPaywall.song?.id}
            />

            <ShareModal
                isOpen={shareModal.isOpen}
                onClose={() => setShareModal({ isOpen: false, song: null })}
                mediaTitle={shareModal.song?.title || 'Unknown Song'}
                mediaType="audio"
                username={shareModal.song?.username || 'band'}
                mediaId={shareModal.song?.stored_filename || shareModal.song?.original_filename || shareModal.song?.id}
                artistName={shareModal.song?.display_name || shareModal.song?.username || 'band'}
            />

            <SignupInviteModal
                isOpen={signupInvite.isOpen}
                onClose={() => setSignupInvite({ isOpen: false, contentType: 'audio' })}
                contentType={signupInvite.contentType}
            />

            <AccessRequiredModal
                isOpen={accessModal.isOpen}
                onClose={() => setAccessModal({ isOpen: false, song: null })}
                accessLevel={accessModal.song?.access_level}
                action="listen"
                isAuthenticated={isAuthenticated}
                viewUrl={accessModal.song ? `/view/audio/${accessModal.song.username || 'band'}/${accessModal.song.stored_filename || accessModal.song.original_filename || accessModal.song.id}` : null}
            />
        </div>
    );
};

export default LatestDeepWaveSongs;
